const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_VIDEO_BYTES = 50 * 1024 * 1024; // 50 MB

function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = String(value ?? '');
  return div.innerHTML;
}

// Review text is stored as plain text with literal line breaks (e.g. from a
// <textarea>) — split on those so each line renders as its own <p> instead of
// collapsing into one run-on paragraph.
function reviewTextHtml(text) {
  return String(text ?? '')
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => `<p class="body-sm mt-1.5">${escapeHtml(line)}</p>`)
    .join('');
}

// Review photos are arbitrary uploads with no known dimensions (unlike Shopify
// product images), so PhotoSwipe needs each one measured before it can size the
// slide correctly instead of stretching it to fill the viewport.
function hydratePswpDimensions(container) {
  container.querySelectorAll('.js-review-image-zoom:not([data-pswp-width])').forEach((anchor) => {
    const url = anchor.getAttribute('data-pswp-src');
    if (!url) return;
    const img = new Image();
    img.onload = () => {
      anchor.setAttribute('data-pswp-width', String(img.naturalWidth));
      anchor.setAttribute('data-pswp-height', String(img.naturalHeight));
    };
    img.src = url;
  });
}

function starsHtml(config, rating, size) {
  const filled = size === 'lg' ? config.starFilledSvgLg : config.starFilledSvg;
  const half = size === 'lg' ? config.starHalfSvgLg : config.starHalfSvg;
  const empty = size === 'lg' ? config.starEmptySvgLg : config.starEmptySvg;

  const full = Math.floor(rating);
  const hasHalf = rating - full >= 0.25 && rating - full < 0.75;
  const roundedFull = hasHalf ? full : Math.round(rating);

  let html = '';
  for (let i = 0; i < 5; i++) {
    if (i < roundedFull) html += filled || '';
    else if (i === roundedFull && hasHalf) html += half || '';
    else html += empty || '';
  }
  return html;
}

function formatRelativeDate(isoString) {
  const then = new Date(isoString).getTime();
  const now = Date.now();
  const diffSeconds = Math.max(0, Math.floor((now - then) / 1000));

  const units = [
    { unit: 'year', seconds: 31536000 },
    { unit: 'month', seconds: 2592000 },
    { unit: 'week', seconds: 604800 },
    { unit: 'day', seconds: 86400 },
    { unit: 'hour', seconds: 3600 },
    { unit: 'minute', seconds: 60 },
  ];

  for (const { unit, seconds } of units) {
    const value = Math.floor(diffSeconds / seconds);
    if (value >= 1) return `${value} ${unit}${value > 1 ? 's' : ''} ago`;
  }
  return 'just now';
}

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

class ProductReviews extends HTMLElement {
  connectedCallback() {
    this._config = this._readConfig();
    this._page = 1;
    this._perPage = Number(this.dataset.perPage) || 5;
    this._bindWriteReviewButtons();
    this._fetchPage(1);
    this._initPhotoSwipe();
  }

  disconnectedCallback() {
    if (this.photoSwipeLightbox) {
      this.photoSwipeLightbox.destroy();
      this.photoSwipeLightbox = null;
    }
  }

  async _initPhotoSwipe() {
    if (!this.dataset.photoswipeUrl || !this.dataset.photoswipeLightboxUrl) return;

    try {
      const PhotoSwipe = (await import(this.dataset.photoswipeUrl)).default;
      const PhotoSwipeLightbox = (await import(this.dataset.photoswipeLightboxUrl)).default;
      if (!this.isConnected) return;

      // Delegated on `this`, so re-rendered review/photo markup (page changes,
      // fetch refreshes) is picked up without re-initializing the lightbox.
      this.photoSwipeLightbox = new PhotoSwipeLightbox({
        gallery: this,
        children: '.js-review-image-zoom',
        pswpModule: PhotoSwipe,
        initialZoomLevel: 'fit',
        secondaryZoomLevel: 2,
        maxZoomLevel: 4,
        loop: false,
      });
      this.photoSwipeLightbox.init();
    } catch (error) {
      console.error('PhotoSwipe initialization error:', error);
    }
  }

  _readConfig() {
    const script = this.querySelector('[data-config-json]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent || '{}');
    } catch {
      return {};
    }
  }

  _fetchPage(page) {
    const endpoint = this.dataset.endpoint;
    const productId = this.dataset.productId;
    const qs = new URLSearchParams({
      product_id: productId,
      page: String(page),
      per_page: String(this._perPage),
    });

    fetch(`${endpoint}?${qs}`)
      .then((r) => {
        if (!r.ok) throw new Error(`reviews failed: ${r.status}`);
        return r.json();
      })
      .then((data) => {
        this._page = data.page || page;
        this._render(data);
      })
      .catch(() => {
        // Fetch/parse failed (e.g. app proxy unreachable) — fall back to the
        // no-reviews state so the "Write a review" button is never just missing.
        this._render({ reviews: [], total: 0, page: 1, per_page: this._perPage, avg_rating: 0, breakdown: [], photos: [] });
      });
  }

  // Drops the loading skeleton. Called from _render, which runs on both the success and the
  // failure path, so a failed fetch lands on the real no-reviews state rather than shimmering
  // forever. Removing an already-absent attribute is a no-op, so page changes are unaffected.
  _stopLoading() {
    this.removeAttribute('data-loading');
    this.removeAttribute('aria-busy');
  }

  _render(data) {
    this._stopLoading();

    const summaryEl = this.querySelector('[data-stat="summary"]');
    const emptyEl = this.querySelector('[data-stat="no-reviews"]');
    if (!summaryEl || !emptyEl) return;

    if (!data.total) {
      summaryEl.classList.add('hidden');
      emptyEl.classList.remove('hidden');
      return;
    }

    emptyEl.classList.add('hidden');
    summaryEl.classList.remove('hidden');

    this._renderSummary(summaryEl, data);
    this._renderBreakdown(summaryEl, data.breakdown || []);
    this._renderPhotos(summaryEl, data.photos || []);
    this._renderReviewList(summaryEl, data.reviews || []);
    this._renderPagination(summaryEl, data);
  }

  _renderSummary(root, data) {
    const avgEl = root.querySelector('[data-stat="avg-rating"]');
    const starsEl = root.querySelector('[data-stat="avg-stars"]');
    const countInlineEl = root.querySelector('[data-stat="review-count-inline"]');
    const countEl = root.querySelector('[data-stat="review-count"]');
    const template = this._config.reviewsCountTemplate || '%count% reviews';
    const countText = template.replace('%count%', String(data.total));

    if (avgEl) avgEl.textContent = (data.avg_rating || 0).toFixed(1);
    if (starsEl) starsEl.innerHTML = starsHtml(this._config, data.avg_rating || 0, 'lg');
    if (countInlineEl) countInlineEl.textContent = `· ${countText}`;
    if (countEl) countEl.textContent = countText;
  }

  _renderBreakdown(root, breakdown) {
    const el = root.querySelector('[data-stat="breakdown"]');
    if (!el) return;
    el.innerHTML = breakdown
      .map(
        (row) => `
          <div class="product-reviews__breakdown-row">
            <span class="body-xs" style="width:12px;">${row.stars}</span>
            <div class="product-reviews__breakdown-track">
              <div class="product-reviews__breakdown-fill" style="width:${row.pct}%;"></div>
            </div>
            <span class="body-xs text-text-muted" style="width:32px;text-align:right;">${row.count}</span>
          </div>
        `,
      )
      .join('');
  }

  _renderPhotos(root, photos) {
    const el = root.querySelector('[data-stat="photos"]');
    if (!el) return;
    const alt = escapeHtml(this._config.customerPhotoAlt || '');
    el.innerHTML = photos
      .map(
        (url) => `
          <a class="js-review-image-zoom product-reviews__photo cursor-zoom-in" href="${escapeHtml(url)}" data-pswp-src="${escapeHtml(url)}" style="background-image:url('${escapeHtml(url)}')" role="img" aria-label="${alt}"></a>
        `,
      )
      .join('');
    hydratePswpDimensions(el);
  }

  _renderReviewList(root, reviews) {
    const el = root.querySelector('[data-stat="review-list"]');
    if (!el) return;
    const photoAlt = escapeHtml(this._config.reviewPhotoAlt || '');

    el.innerHTML = reviews
      .map((rev) => {
        const images = rev.media_urls || [];
        return `
          <div class="product-reviews__review-item">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2.5">
                <span class="product-reviews__avatar">${escapeHtml(initials(rev.reviewer_name))}</span>
                <span class="body-sm font-semibold">${escapeHtml(rev.reviewer_name)}</span>
              </div>
              <span class="body-xs text-text-muted">${escapeHtml(formatRelativeDate(rev.created_at))}</span>
            </div>
            <div class="flex items-center gap-0.5 mt-3">${starsHtml(this._config, rev.rating, 'sm')}</div>
            ${rev.subject ? `<div class="body-base font-semibold mt-2.5">${escapeHtml(rev.subject)}</div>` : ''}
            ${rev.review_text ? reviewTextHtml(rev.review_text) : ''}
            ${
              images.length
                ? `<div class="flex flex-wrap gap-2.5 mt-3.5">${images
                    .map(
                      (url) =>
                        `<a class="js-review-image-zoom product-reviews__review-photo cursor-zoom-in" href="${escapeHtml(url)}" data-pswp-src="${escapeHtml(url)}" style="background-image:url('${escapeHtml(url)}')" role="img" aria-label="${photoAlt}"></a>`,
                    )
                    .join('')}</div>`
                : ''
            }
          </div>
        `;
      })
      .join('');
    hydratePswpDimensions(el);
  }

  _renderPagination(root, data) {
    const el = root.querySelector('[data-stat="pagination"]');
    if (!el) return;

    const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.per_page || this._perPage)));
    const page = data.page || 1;
    if (totalPages <= 1) {
      el.innerHTML = '';
      return;
    }

    const prevLabel = escapeHtml(this._config.prevLabel || '‹');
    const nextLabel = escapeHtml(this._config.nextLabel || '›');

    const windowStart = Math.max(1, Math.min(page - 2, totalPages - 4));
    const windowEnd = Math.min(totalPages, Math.max(page + 2, 5));
    const pages = [];
    for (let p = windowStart; p <= windowEnd; p++) pages.push(p);

    const numberButtons = pages
      .map(
        (p) => `
          <button type="button" class="product-reviews__pagebtn" data-page="${p}" aria-current="${p === page}">${p}</button>
        `,
      )
      .join('');

    el.innerHTML = `
      <button type="button" class="product-reviews__pagebtn" data-page="${page - 1}" ${page <= 1 ? 'disabled' : ''} aria-label="${prevLabel}">‹</button>
      ${numberButtons}
      <button type="button" class="product-reviews__pagebtn" data-page="${page + 1}" ${page >= totalPages ? 'disabled' : ''} aria-label="${nextLabel}">›</button>
    `;

    el.querySelectorAll('[data-page]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = Number(btn.getAttribute('data-page'));
        if (target >= 1 && target <= totalPages) this._fetchPage(target);
      });
    });
  }

  _bindWriteReviewButtons() {
    this.querySelectorAll('[data-action="write-review"]').forEach((btn) => {
      btn.addEventListener('click', () => this._openWriteReviewModal());
    });
  }

  _openWriteReviewModal() {
    const dialogModal = document.getElementById('DialogModal');
    const template = this.querySelector('[data-review-form-template]');
    if (
      !dialogModal ||
      !template ||
      typeof dialogModal.setContent !== 'function' ||
      typeof dialogModal.openModal !== 'function'
    ) {
      return;
    }

    if (typeof dialogModal.setSize === 'function') dialogModal.setSize('small');
    dialogModal.setContent(template.innerHTML);
    dialogModal.openModal();

    const root = dialogModal.querySelector('[data-review-form-root]');
    if (root) this._initWriteReviewForm(root);
  }

  _initWriteReviewForm(root) {
    const form = root.querySelector('[data-review-form]');
    if (!form) return; // logged-out state has no form

    this._initRating(root);
    this._initMediaUpload(root);

    const btn = form.querySelector('[data-review-btn]');
    const statusEl = form.querySelector('[data-review-status]');
    const endpoint = this.dataset.endpoint;
    const productId = this.dataset.productId;
    const productTitle = this.dataset.productTitle || '';
    const strings = this._config.form || {};

    const setStatus = (state, msg) => {
      statusEl.setAttribute('data-state', state);
      statusEl.textContent = msg;
    };

    const errorMessage = (status, data) => {
      if (status === 401) return strings.errorLoginRequired || '';
      if (status === 403) return strings.errorNotPurchased || '';
      if (status === 404) return strings.errorAccountNotFound || '';
      if (status === 409) return strings.errorAlreadyReviewed || '';
      if (data && data.detail) {
        if (typeof data.detail === 'string') return data.detail;
        if (data.detail.error) return String(data.detail.error);
      }
      if (data && data.error) return String(data.error);
      return strings.errorGeneric || '';
    };

    const validateFiles = (files) => {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const isVideo = f.type.indexOf('video/') === 0;
        const isImage = f.type.indexOf('image/') === 0;
        if (!isVideo && !isImage) return (strings.errorInvalidFile || '').replace('%filename%', f.name);
        const limit = isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
        if (f.size > limit) {
          const template = isVideo ? strings.errorFileTooLargeVideo : strings.errorFileTooLargeImage;
          return (template || '').replace('%filename%', f.name);
        }
      }
      return null;
    };

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const name = (form.querySelector('[name="reviewer_name"]').value || '').trim();
      const rating = form.querySelector('[name="rating"]').value;
      const fileInput = form.querySelector('[name="files"]');
      const files = fileInput && fileInput.files ? fileInput.files : [];

      if (!name) return setStatus('error', strings.errorNameRequired || '');
      if (!rating) return setStatus('error', strings.errorRatingRequired || '');
      const fileError = validateFiles(files);
      if (fileError) return setStatus('error', fileError);

      const fd = new FormData();
      fd.append('shopify_product_id', productId);
      fd.append('product_title', productTitle);
      fd.append('reviewer_name', name);
      fd.append('subject', form.querySelector('[name="subject"]').value || '');
      fd.append('review_text', form.querySelector('[name="review_text"]').value || '');
      fd.append('rating', rating);
      for (let i = 0; i < files.length; i++) fd.append('files', files[i]);

      btn.disabled = true;
      setStatus('info', strings.submittingMessage || '');
      try {
        const res = await fetch(endpoint, { method: 'POST', body: fd });
        let data = {};
        try {
          data = await res.json();
        } catch {
          // non-JSON response
        }

        if (!res.ok) {
          setStatus('error', errorMessage(res.status, data));
          btn.disabled = false;
        } else {
          setStatus('success', strings.submitSuccess || '');
          form.reset();
          const hidden = form.querySelector('[name="rating"]');
          if (hidden) hidden.value = '';
          this._paintStars(root, 0);
          this._clearMediaPreviews(root);
          this._fetchPage(this._page);
        }
      } catch {
        setStatus('error', strings.errorNetwork || '');
        btn.disabled = false;
      }
    });
  }

  _initRating(root) {
    const wrap = root.querySelector('[data-review-rating]');
    if (!wrap) return;
    const hidden = wrap.querySelector('input[name="rating"]');
    this._paintStars(root, 0);

    wrap.querySelectorAll('[data-star]').forEach((s) => {
      s.addEventListener('click', () => {
        const value = Number(s.getAttribute('data-star'));
        hidden.value = String(value);
        this._paintStars(root, value);
      });
    });
  }

  _initMediaUpload(root) {
    const input = root.querySelector('[data-media-input]');
    const previewsEl = root.querySelector('[data-media-previews]');
    if (!input || !previewsEl) return;

    this._mediaObjectUrls = this._mediaObjectUrls || [];

    input.addEventListener('change', () => {
      this._renderMediaPreviews(previewsEl, input.files);
    });
  }

  _renderMediaPreviews(previewsEl, files) {
    this._revokeMediaObjectUrls();
    previewsEl.innerHTML = '';

    Array.from(files || []).forEach((file) => {
      const isImage = file.type.indexOf('image/') === 0;
      const isVideo = file.type.indexOf('video/') === 0;
      const preview = document.createElement('div');
      preview.className = 'product-reviews__media-preview';

      if (isImage || isVideo) {
        const url = URL.createObjectURL(file);
        this._mediaObjectUrls.push(url);
        const el = document.createElement(isImage ? 'img' : 'video');
        el.src = url;
        if (!isImage) {
          el.muted = true;
          el.playsInline = true;
          el.preload = 'metadata';
        }
        preview.appendChild(el);
      } else {
        const span = document.createElement('span');
        span.textContent = file.name;
        preview.appendChild(span);
      }

      previewsEl.appendChild(preview);
    });
  }

  _clearMediaPreviews(root) {
    const previewsEl = root.querySelector('[data-media-previews]');
    if (previewsEl) previewsEl.innerHTML = '';
    this._revokeMediaObjectUrls();
  }

  _revokeMediaObjectUrls() {
    (this._mediaObjectUrls || []).forEach((url) => URL.revokeObjectURL(url));
    this._mediaObjectUrls = [];
  }

  _paintStars(root, value) {
    const strings = this._config.form || {};
    root.querySelectorAll('[data-star]').forEach((s) => {
      const starValue = Number(s.getAttribute('data-star'));
      s.innerHTML = starValue <= value ? strings.starFilledSvg || '★' : strings.starEmptySvg || '☆';
    });
  }
}

if (!customElements.get('product-reviews')) {
  customElements.define('product-reviews', ProductReviews);
}
